import type { MockJob, MockUrlResult, UrlStatus } from '../types/accuracy';

/** Hand-crafted URLs for accuracy metric demos (FP/FN/WB). */
const DEMO_URL_RESULTS: MockUrlResult[] = [
  {
    id: 'c5a8af8d-59db-4f49-9068-7556b45dcaf3',
    url: 'https://xn--lckh1a7bzah4vue7290au22cbvar664a.com/',
    status: 'reviewed',
    category_content_existence_entries: {
      overseas_travel_insurance: { value: true, system_value: true, source: 'system' },
      interest_rate: { value: false, system_value: true, source: 'user' },
    },
    violations: [
      {
        id: 'ec24a570-b02b-4e97-be32-e0e4edcbe51b',
        category: 'overseas_travel_insurance',
        severity: 'medium',
        violation_type: 'generated',
        violation_content: '「海外旅行\\n傷害保険」の記載あり',
        deleted: true,
        x_ratio: 0.3965,
        y_ratio: 0.0742,
        width_ratio: 0.0399,
        height_ratio: 0.5153,
      },
      {
        id: 'wb-demo-1',
        category: 'interest_rate',
        severity: 'high',
        violation_type: 'generated',
        violation_content: '年利表示が不正確',
        deleted: false,
        x_ratio: 0.2,
        y_ratio: 0.3,
        width_ratio: 0.25,
        height_ratio: 0.1,
        detection_x_ratio: 0.18,
        detection_y_ratio: 0.28,
        detection_width_ratio: 0.22,
        detection_height_ratio: 0.09,
      },
      {
        id: 'fn-demo-1',
        category: 'interest_rate',
        severity: 'medium',
        violation_type: 'manual_created',
        violation_content: '上限金利の注釈が不足',
        deleted: false,
        x_ratio: 0.55,
        y_ratio: 0.62,
        width_ratio: 0.35,
        height_ratio: 0.12,
      },
    ],
  },
];

/** Number of generated dummy URLs for the run-page preview table. */
export const MOCK_DUMMY_URL_COUNT = 200;

function buildDummyUrlResults(count: number): MockUrlResult[] {
  return Array.from({ length: count }, (_, index) => {
    const n = index + 1;
    const pad = String(n).padStart(3, '0');
    const status: UrlStatus = 'reviewed';
    const hasViolations = n % 4 !== 0;

    return {
      id: `dummy-url-${pad}`,
      url: `https://affiliate-preview-${pad}.example.co.jp/campaign/lp?utm=dummy`,
      status,
      category_content_existence_entries: {
        overseas_travel_insurance: {
          value: n % 5 !== 0,
          system_value: n % 7 !== 0,
          source: n % 3 === 0 ? 'user' : 'system',
        },
        interest_rate: {
          value: n % 2 === 0,
          system_value: n % 2 === 0,
          source: 'system',
        },
      },
      violations: hasViolations
        ? [
            {
              id: `dummy-v-${pad}-1`,
              category: n % 2 === 0 ? 'interest_rate' : 'overseas_travel_insurance',
              severity: 'medium',
              violation_type: 'generated',
              violation_content: `ダミー違反 #${pad}`,
              deleted: n % 9 === 0,
              x_ratio: 0.1,
              y_ratio: 0.2,
              width_ratio: 0.3,
              height_ratio: 0.1,
            },
          ]
        : [],
    };
  });
}

/** Mock jobs (structure inspired by jobs.json; standalone for UI preview). */
export const MOCK_JOBS: MockJob[] = [
  {
    id: 'ecae36bb-e113-4028-8d64-6570c57e4d29',
    job_name: 'test',
    guideline_id: 'c4a74ed4-594f-4fe0-a259-9f6a7c0248bb',
    version: 36,
    url_process_results: [...DEMO_URL_RESULTS],
  },
  {
    id: 'mock-job-bulk-200',
    job_name: 'dummy-200-urls',
    guideline_id: 'c4a74ed4-594f-4fe0-a259-9f6a7c0248bb',
    version: 36,
    url_process_results: buildDummyUrlResults(MOCK_DUMMY_URL_COUNT),
  },
  {
    id: 'mock-job-accom-v12',
    job_name: 'accom-5月キャンペーン',
    guideline_id: 'd8b2e901-1111-4000-8000-000000000002',
    version: 12,
    url_process_results: [
      {
        id: 'accom-url-1',
        url: 'https://accom-preview.example.jp/lp/may',
        status: 'reviewed',
        category_content_existence_entries: {
          interest_rate: { value: true, system_value: true, source: 'system' },
        },
        violations: [],
      },
      {
        id: 'accom-url-2',
        url: 'https://accom-preview.example.jp/lp/june',
        status: 'review_pending',
        category_content_existence_entries: {
          interest_rate: { value: false, system_value: false, source: 'system' },
        },
        violations: [],
      },
    ],
  },
  {
    id: 'mock-job-2',
    job_name: 'sample-job',
    guideline_id: 'c4a74ed4-594f-4fe0-a259-9f6a7c0248bb',
    version: 36,
    url_process_results: [
      {
        id: 'mock-url-2',
        url: 'https://example.com/lp',
        status: 'reviewed',
        category_content_existence_entries: {
          interest_rate: { value: true, system_value: false, source: 'user' },
        },
        violations: [
          {
            id: 'tp-legacy-1',
            category: 'interest_rate',
            severity: 'low',
            violation_type: 'generated',
            violation_content: '金利注釈の位置が不明瞭',
            deleted: false,
            x_ratio: 0.1,
            y_ratio: 0.82,
            width_ratio: 0.5,
            height_ratio: 0.1,
          },
        ],
      },
    ],
  },
];

export const MOCK_CATEGORY_TITLES: Record<string, string> = {
  overseas_travel_insurance: '海外旅行傷害保険',
  interest_rate: '金利表記',
};

export const DEFAULT_GUIDELINE_ID = 'c4a74ed4-594f-4fe0-a259-9f6a7c0248bb';
export const DEFAULT_GUIDELINE_VERSION = 36;

export const MOCK_GUIDELINE_ACCOM_ID = 'd8b2e901-1111-4000-8000-000000000002';

/** Dummy guideline catalog for レポートを作成 UI. */
export const MOCK_GUIDELINES = [
  {
    id: DEFAULT_GUIDELINE_ID,
    name: 'nikosu',
    versions: [36, 35],
  },
  {
    id: MOCK_GUIDELINE_ACCOM_ID,
    name: 'accom',
    versions: [12],
  },
] as const;
